var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, P, PCSS, Watch, PropertyConverters, Utils, CustomElementUtils, ViewChild } from '@pacem/pacem-core';
import { PacemBaseCellElement } from './types';
//namespace Pacem.Components.Plus {
export const DataRowSelectChangeEventName = "datarowselectchange";
export class DataRowSelectChangeEvent extends CustomEvent {
    constructor(detail) {
        super(DataRowSelectChangeEventName, { detail, bubbles: false, cancelable: true });
    }
}
const EMPTY_CSS = {};
const EMPTY_CSSCLASS = [];
let PacemDataCellElement = class PacemDataCellElement extends PacemBaseCellElement {
    updateLayout(metadata = this.metadata) {
        this._setup(metadata);
    }
    connectedCallback() {
        super.connectedCallback();
        Utils.addClass(this, PCSS + '-datacell');
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first) {
            switch (name) {
                case '_contentTooltip':
                    this._updateTooltipJustInCase();
                    break;
                case 'gridState':
                case 'rowindex':
                    this._updateSelection();
                    break;
            }
        }
    }
    _updateTooltipJustInCase() {
        const tip = this.metadata.dataCell?.autoTooltip ?? true;
        if (tip && !Utils.isNullOrEmpty(this._contentTooltip)) {
            this.setAttribute('tooltip', this._contentTooltip);
        }
        else {
            this.removeAttribute('tooltip');
        }
    }
    _inflateContent(item = this.dataitem, index = this.rowindex, metadata = this.metadata) {
        this._contentTooltip = '';
        let format = metadata.dataCell?.format;
        const nullValue = metadata.dataCell?.null ?? '';
        // is 'format' a custom function?
        if (typeof format === 'function') {
            const htmlOrNode = format(this, metadata, item, index);
            if (Utils.isNullOrEmpty(htmlOrNode)) {
                return nullValue;
            }
            if (typeof htmlOrNode === 'string') {
                return htmlOrNode;
            }
            return htmlOrNode;
        }
        // standard column
        const tget = CustomElementUtils.resolvePath(metadata.prop, item)?.target;
        const plainValue = tget?.value;
        if (Utils.isNullOrEmpty(plainValue)) {
            return nullValue;
        }
        const culture = Utils.lang(this);
        switch (metadata.dataType) {
            case "boolean":
                return `<${P}-icon icon="material ${(!plainValue ? 'radio_button_unchecked' : 'check_circle_outline')} text-big-steady"></${P}-icon>`;
            case "time":
            case "date":
            case "datetime":
                return this._contentTooltip = Utils.core.date(plainValue, format, culture);
            case "byte":
            case "int16":
            case "int32":
            case "int64":
            case "short":
            case "integer":
            case "int":
            case "long":
            case "double":
            case "decimal":
            case "float":
            case "single":
            case "number":
                return this._contentTooltip = Utils.core.number(plainValue, format || '', culture);
            case "currency":
                format ??= 'EUR';
                if (typeof format === 'string') {
                    return this._contentTooltip = Utils.core.currency(plainValue, format, culture);
                }
                return this._contentTooltip = Utils.core.number(plainValue, format, culture);
            case "percent":
            case "percentage":
                format ??= { style: 'percent', maximumFractionDigits: 2 };
                if (typeof format === 'string') {
                    return this._contentTooltip = Utils.core.currency(plainValue, format, culture);
                }
                return this._contentTooltip = Utils.core.number(plainValue, format, culture);
            case "url":
            case "emailaddress":
            case "phonenumber":
            // TODO: render links
            default:
                return this._contentTooltip = plainValue;
        }
    }
    // @Debounce(true)
    _inflateCssClass(item = this.dataitem, index = this.rowindex, metadata = this.metadata, datasource) {
        if (Utils.isNull(item) || Utils.isNullOrEmpty(metadata)) {
            return EMPTY_CSSCLASS;
        }
        const cssClass = metadata.dataCell && metadata.dataCell.cssClass || EMPTY_CSSCLASS;
        const retval = [];
        if (typeof cssClass === 'function') {
            Array.prototype.push.apply(retval, cssClass(this, metadata, item, index) || []);
        }
        else {
            Array.prototype.push.apply(retval, cssClass);
        }
        // alignment
        switch (metadata.dataCell?.verticalAlign?.toLowerCase()) {
            case 'top':
                retval.push('datacell-top');
                break;
            case 'bottom':
                retval.push('datacell-bottom');
                break;
            case 'middle':
                retval.push('datacell-middle');
                break;
        }
        switch (metadata.dataCell?.horizontalAlign?.toLowerCase()) {
            // headcell display is flex: set h-alignment at container-level
            case 'left':
                retval.push('datacell-left');
                break;
            case 'right':
                retval.push('datacell-right');
                break;
            case 'center':
                retval.push('datacell-center');
                break;
        }
        if (datasource?.length) {
            if (index === 0) {
                retval.push('datacell-firstrow');
            }
            if (index === (datasource.length - 1)) {
                retval.push('datacell-lastrow');
            }
        }
        return retval;
    }
    _inflateCss(item = this.dataitem, index = this.rowindex, metadata = this.metadata) {
        if (Utils.isNull(item) || Utils.isNullOrEmpty(metadata)) {
            return EMPTY_CSS;
        }
        const css = metadata.dataCell && metadata.dataCell.css || EMPTY_CSS;
        if (typeof css === 'function') {
            return css(this, metadata, item, index);
        }
        return css;
    }
    _setup(metadata = this.metadata) {
        this.setAttribute("css-class", "{{ this._inflateCssClass(^item, ^index, this.metadata, :host.datasource) }}");
        this.setAttribute("css", "{{ Pacem.Utils.extend({ 'grid-row': ^index+2 }, this._inflateCss(^item, ^index, this.metadata, :host.datasource)) }}");
        const truncate = !!(metadata.dataCell?.textEllipsis);
        const contentCssClass = truncate ? ['text-truncate'] : [];
        const selectMode = metadata?.dataCell?.selectMode?.toLowerCase();
        switch (selectMode) {
            case 'cell':
            case 'row':
                // make the content wrapper inert so that the click event can bubble
                contentCssClass.push(PCSS + '-inert');
                contentCssClass.push(PCSS + '-unselect');
                break;
        }
        this._content.cssClass = contentCssClass;
        this._updateSelection();
    }
    _updateSelection() {
        const selectedRows = this.gridState?.selectedRows ?? [];
        if (selectedRows.indexOf(this.rowindex) >= 0) {
            Utils.addClass(this, 'datarow-selected datacell-filled datacell-primary');
        }
        else {
            Utils.removeClass(this, 'datarow-selected datacell-filled datacell-primary');
        }
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Eval })
], PacemDataCellElement.prototype, "dataitem", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemDataCellElement.prototype, "rowindex", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String, debounce: 200 })
], PacemDataCellElement.prototype, "_contentTooltip", void 0);
__decorate([
    ViewChild(P + '-span')
], PacemDataCellElement.prototype, "_content", void 0);
PacemDataCellElement = __decorate([
    CustomElement({ tagName: P + '-datacell', shadow: false, template: `<${P}-span content="{{ :host._inflateContent(^item, ^index, :host.metadata) }}"></${P}-span>` })
], PacemDataCellElement);
export { PacemDataCellElement };
